/**
 * "Real tissue" look for the laparoscope: the procedurally painted anatomy
 * keeps its shapes and shading, but broad tissue colours are swapped for
 * texels sampled from pixelated photographs (liver, fat, gallbladder wall,
 * omentum). Thin structures the gameplay depends on (ducts, vessels, blood,
 * instruments, gauze) keep their drawn colours.
 */
import { type Ctx, isEn, MED_IMAGES, type MedImageId, medImage, tr } from "../../engine";

type Tissue = "liver" | "fat" | "serosa" | "omentum";

const TEX: Record<Tissue, MedImageId> = {
  liver: "tex_liver",
  fat: "tex_fat",
  serosa: "tex_gb",
  omentum: "tex_omentum",
};

interface Texels {
  w: number;
  h: number;
  /** Per-texel brightness relative to its neighbourhood (≈1), so lighting gradients in the photo don't tile. */
  detail: Float32Array;
  /** Mean colour and luminance (0–1) of the photo crop. */
  col: [number, number, number];
  mean: number;
}

/** How much of the photo's colour (cw) and fine detail (dw) each tissue takes. */
const MIX: Record<Tissue, { cw: number; dw: number }> = {
  liver: { cw: 0.65, dw: 1 },
  serosa: { cw: 0.35, dw: 0.85 },
  fat: { cw: 0.15, dw: 0.9 },
  omentum: { cw: 0.3, dw: 1 },
};

const texels = new Map<Tissue, Texels>();

const lum = (r: number, g: number, b: number): number => (0.299 * r + 0.587 * g + 0.114 * b) / 255;

function load(t: Tissue): Texels | null {
  const hit = texels.get(t);
  if (hit) return hit;
  const img = medImage(TEX[t]);
  if (!img) return null;
  const w = img.naturalWidth;
  const h = img.naturalHeight;
  const cv = document.createElement("canvas");
  cv.width = w;
  cv.height = h;
  const c = cv.getContext("2d");
  if (!c) return null;
  c.drawImage(img, 0, 0);
  const d = c.getImageData(0, 0, w, h).data;
  const L = new Float32Array(w * h);
  const col: [number, number, number] = [0, 0, 0];
  for (let i = 0; i < w * h; i++) {
    const r = d[i * 4] ?? 0;
    const g = d[i * 4 + 1] ?? 0;
    const b = d[i * 4 + 2] ?? 0;
    L[i] = lum(r, g, b);
    col[0] += r;
    col[1] += g;
    col[2] += b;
  }
  const n = w * h;
  col[0] /= n;
  col[1] /= n;
  col[2] /= n;
  // High-pass: divide by a wrapped box blur.
  const R = 5;
  const detail = new Float32Array(n);
  for (let y = 0; y < h; y++)
    for (let x = 0; x < w; x++) {
      let sum = 0;
      for (let dy = -R; dy <= R; dy++)
        for (let dx = -R; dx <= R; dx++) sum += L[((y + dy + h) % h) * w + ((x + dx + w) % w)] ?? 0;
      const local = sum / ((2 * R + 1) * (2 * R + 1)) || 0.5;
      detail[y * w + x] = Math.max(0.5, Math.min(1.5, (L[y * w + x] ?? 0) / local));
    }
  const tx = { w, h, detail, col, mean: lum(col[0], col[1], col[2]) || 0.5 };
  texels.set(t, tx);
  return tx;
}

/** Mirrored tiling, so tile edges meet their own reflection instead of a seam. */
const mirror = (v: number, n: number): number => {
  const m = v % (2 * n);
  return m < n ? m : 2 * n - 1 - m;
};

/** True once every texture has loaded (part of the scope's world cache key). */
export function tissueReady(): boolean {
  return (Object.keys(TEX) as Tissue[]).every((t) => load(t) !== null);
}

/** "Hue, saturation, lightness" with hue in degrees. */
function hsl(r: number, g: number, b: number): [number, number, number] {
  const R = r / 255;
  const G = g / 255;
  const B = b / 255;
  const mx = Math.max(R, G, B);
  const mn = Math.min(R, G, B);
  const l = (mx + mn) / 2;
  if (mx === mn) return [0, 0, l];
  const dd = mx - mn;
  const s = l > 0.5 ? dd / (2 - mx - mn) : dd / (mx + mn);
  let h: number;
  if (mx === R) h = ((G - B) / dd + (G < B ? 6 : 0)) * 60;
  else if (mx === G) h = ((B - R) / dd + 2) * 60;
  else h = ((R - G) / dd + 4) * 60;
  return [h, s, l];
}

/** Which tissue a drawn colour stands for, or null to leave it alone. */
function classify(r: number, g: number, b: number): Tissue | null {
  const [h, s, l] = hsl(r, g, b);
  if (l < 0.18 || s < 0.12 || l > 0.9) return null; // cavity, shadows, steel, gauze white, highlights
  const reddish = h >= 340 || h < 22;
  // Bright saturated red is blood: keep it.
  if (reddish && s > 0.62 && l > 0.3) return null;
  // Pale pink serosa: bowel, uterus, tubes, ovaries, duodenum.
  if (reddish && l >= 0.5) return "serosa";
  // Red-brown parenchyma: liver; also the deep pelvic peritoneum.
  if (reddish) return "liver";
  // Tan colon / omentum.
  if (h >= 22 && h < 36 && l >= 0.45) return "omentum";
  // Yellow fat (omentum, hepatocystic fat, falciform ligament).
  if (h >= 36 && h < 58 && s >= 0.35 && l >= 0.45) return "fat";
  // Olive, low-saturation gallbladder wall (bile and the pale ducts stay drawn).
  if (h >= 58 && h < 110 && s < 0.35 && l < 0.7) return "serosa";
  return null;
}

/**
 * Re-skin a painted world in place. `keep` lists exact hex colours that must
 * stay as drawn (e.g. the hidden gauze, a thin artery).
 * `strength` blends texture over the drawn colour (0 = drawn, 1 = texture).
 */
export function texturize(c: Ctx, w: number, h: number, keep: readonly string[] = [], strength = 0.85): void {
  const tex = new Map<Tissue, Texels>();
  for (const t of Object.keys(TEX) as Tissue[]) {
    const tx = load(t);
    if (!tx) return;
    tex.set(t, tx);
  }
  const kept = new Set(keep.map((x) => x.toLowerCase()));
  const img = c.getImageData(0, 0, w, h);
  const d = img.data;
  const memo = new Map<number, Tissue | null>();
  for (let y = 0; y < h; y++)
    for (let x = 0; x < w; x++) {
      const i = (y * w + x) * 4;
      const r = d[i] ?? 0;
      const g = d[i + 1] ?? 0;
      const b = d[i + 2] ?? 0;
      const k = (r << 16) | (g << 8) | b;
      let cls = memo.get(k);
      if (cls === undefined) {
        cls = kept.has("#" + k.toString(16).padStart(6, "0")) ? null : classify(r, g, b);
        memo.set(k, cls);
      }
      if (!cls) continue;
      const tx = tex.get(cls);
      if (!tx) continue;
      const { cw, dw } = MIX[cls];
      const det = 1 + ((tx.detail[mirror(y, tx.h) * tx.w + mirror(x, tx.w)] ?? 1) - 1) * dw;
      // The photo's mean colour, brought to the drawn pixel's brightness, blended in; then the photo's fine detail.
      const ratio = Math.min(1.8, lum(r, g, b) / tx.mean);
      for (let ch = 0; ch < 3; ch++) {
        const orig = d[i + ch] ?? 0;
        const photo = Math.min(255, tx.col[ch as 0 | 1 | 2] * ratio);
        const base = orig + (photo - orig) * cw * strength;
        d[i + ch] = base * (1 + (det - 1) * strength);
      }
    }
  c.putImageData(img, 0, 0);
}

/** Licence line for the textures, shown under the scope while textured. */
export function tissueCredit(): string {
  const credits = [...new Set(Object.values<MedImageId>(TEX).map((id) => MED_IMAGES[id].credit))];
  // Credits are stored with a full-width comma (zh); en swaps it for ", ".
  const list = isEn ? credits.map((c) => c.replace(/\uff0c/g, ", ")) : credits;
  return tr(`組織紋理：${list.join("；")}`, `Tissue textures: ${list.join("; ")}`);
}
